import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Stack,
  Divider,
  Typography,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Router,
  CellTower,
  Storage,
  Wifi,
  Analytics,
  Cloud,
  Home,
  Info,
  Public,
  Business,
  People,
  Newspaper,
  Help,
  ContactMail,
  DarkMode,
  LightMode,
  ChevronRight,
  Dashboard,
  NetworkWifi,
  Security,
  SupportAgent,
  Campaign,
  Speed,
} from '@mui/icons-material';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchor, setServicesAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply theme to body - dark mode by default
  useEffect(() => {
    if (isDarkMode) {
      document.body.style.backgroundColor = '#2b2929';
      document.body.style.color = '#FFFFFF';
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.body.style.backgroundColor = '#FFFFFF';
      document.body.style.color = '#1A1A1A';
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleServicesClick = (event) => {
    setServicesAnchor(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchor(null);
    setHoveredService(null);
  };

  const toggleTheme = useCallback(() => {
    setIsDarkMode(!isDarkMode);
  }, [isDarkMode]);

  const navLinks = [
    { name: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
    { name: 'About', path: '/about', icon: <Info sx={{ fontSize: 16 }} /> },
    { name: 'Coverage', path: '/coverage', icon: <Public sx={{ fontSize: 16 }} /> },
    { name: 'Projects', path: '/projects', icon: <Business sx={{ fontSize: 16 }} /> },
    { name: 'Partners', path: '/partners', icon: <People sx={{ fontSize: 16 }} /> },
    { name: 'Sustainability', path: '/sustainability', icon: <Security sx={{ fontSize: 16 }} /> },
    { name: 'Careers', path: '/careers', icon: <SupportAgent sx={{ fontSize: 16 }} /> },
    { name: 'News', path: '/news', icon: <Campaign sx={{ fontSize: 16 }} /> },
    { name: 'FAQs', path: '/faqs', icon: <Help sx={{ fontSize: 16 }} /> },
  ];

  const servicesLinks = [
    { name: 'Fiber Network', path: '/services/fiber-network', icon: <Router sx={{ fontSize: 18 }} />, color: '#e60108', description: 'Ultra-fast fiber connectivity', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
    { name: 'Telecom Towers', path: '/services/telecom-towers', icon: <CellTower sx={{ fontSize: 18 }} />, color: '#e60108', description: '5G ready tower infrastructure', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
    { name: 'Internet Solutions', path: '/services/internet-solutions', icon: <NetworkWifi sx={{ fontSize: 18 }} />, color: '#e60108', description: 'Business internet solutions', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
    { name: 'Data Centers', path: '/services/data-centers', icon: <Storage sx={{ fontSize: 18 }} />, color: '#e60108', description: 'Secure data center services', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
    { name: 'Managed Services', path: '/services/managed-services', icon: <Analytics sx={{ fontSize: 18 }} />, color: '#e60108', description: '24/7 IT management', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
    { name: 'Enterprise Solutions', path: '/services/enterprise-solutions', icon: <Cloud sx={{ fontSize: 18 }} />, color: '#e60108', description: 'Custom enterprise solutions', gradient: 'linear-gradient(135deg, #e60108 0%, #ff4d4d 100%)' },
  ];

  // Get dynamic colors based on dark mode
  const getNavbarBgColor = () => {
    if (isDarkMode) {
      return scrolled 
        ? alpha('#0A0A0A', 0.98) 
        : alpha('#0A0A0A', 0.92);
    }
    return scrolled 
      ? alpha('#FFFFFF', 0.98) 
      : alpha('#FFFFFF', 0.92);
  };

  const getTextColor = () => isDarkMode ? '#FFFFFF' : '#1A1A1A';
  const getHoverColor = () => '#e60108';
  const getDrawerBgColor = () => isDarkMode ? '#0A0A0A' : '#1A1A1A';

  // Mobile Drawer Content
  const drawer = (
    <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: getDrawerBgColor() }}>
      {/* Drawer Header with Logo */}
      <Box sx={{ 
        p: 2.5, 
        borderBottom: `1px solid ${alpha('#FFFFFF', 0.1)}`,
        background: `linear-gradient(135deg, ${alpha('#e60108', 0.05)} 0%, transparent 100%)`,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Box
              component={Link}
              to="/"
              sx={{
                width: 140, // Larger logo in drawer
                height: 140,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img 
                src="/images/wwclogo.png" 
                alt="Worldwide Communication Logo" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                }}
              />
            </Box>
          </motion.div>
          <IconButton 
            onClick={handleDrawerToggle} 
            size="small" 
            sx={{ 
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: alpha('#e60108', 0.1),
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Drawer Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        <List sx={{ py: 0 }}>
          {navLinks.map((link, index) => (
            <motion.div
              key={link.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <ListItem
                component={Link}
                to={link.path}
                onClick={handleDrawerToggle}
                sx={{
                  borderRadius: 2,
                  mb: 0.75,
                  py: 0.75,
                  transition: 'all 0.3s ease',
                  bgcolor: location.pathname === link.path ? alpha('#e60108', 0.15) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha('#e60108', 0.08),
                    transform: 'translateX(6px)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 36, 
                  color: location.pathname === link.path ? '#e60108' : alpha('#FFFFFF', 0.6),
                  transition: 'color 0.3s ease',
                }}>
                  {link.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={link.name} 
                  primaryTypographyProps={{ 
                    fontSize: '0.85rem',
                    fontWeight: location.pathname === link.path ? 600 : 500,
                    color: location.pathname === link.path ? '#e60108' : '#FFFFFF',
                  }} 
                />
                {location.pathname === link.path && (
                  <ChevronRight sx={{ fontSize: 16, color: '#e60108' }} />
                )}
              </ListItem>
            </motion.div>
          ))}
        </List>

        <Divider sx={{ my: 2, borderColor: alpha('#FFFFFF', 0.1) }} />

        {/* Mobile Services Section */}
        <Box>
          <ListItem
            button
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            sx={{ 
              borderRadius: 2, 
              py: 0.75,
              '&:hover': {
                backgroundColor: alpha('#e60108', 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: '#e60108' }}>
              <Dashboard sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText 
              primary="Services" 
              primaryTypographyProps={{ 
                fontSize: '0.85rem', 
                fontWeight: 600, 
                color: '#FFFFFF' 
              }} 
            />
            {mobileServicesOpen ? 
              <ExpandLessIcon sx={{ fontSize: 20, color: '#FFFFFF' }} /> : 
              <ExpandMoreIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
            }
          </ListItem>
          
          <AnimatePresence>
            {mobileServicesOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <List sx={{ pl: 4, py: 0 }}>
                  {servicesLinks.map((service, index) => (
                    <motion.div
                      key={service.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <ListItem
                        component={Link}
                        to={service.path}
                        onClick={handleDrawerToggle}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          py: 0.6,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(service.color, 0.1),
                            transform: 'translateX(6px)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: service.color }}>
                          {service.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={service.name} 
                          primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500, color: '#FFFFFF' }}
                          secondary={service.description}
                          secondaryTypographyProps={{ fontSize: '0.65rem', color: alpha('#FFFFFF', 0.5) }}
                        />
                      </ListItem>
                    </motion.div>
                  ))}
                </List>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Drawer Footer */}
      <Box sx={{ 
        p: 2.5, 
        borderTop: `1px solid ${alpha('#FFFFFF', 0.1)}`,
        background: `linear-gradient(180deg, transparent 0%, ${alpha('#e60108', 0.05)} 100%)`,
      }}>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          fullWidth
          onClick={handleDrawerToggle}
          startIcon={<ContactMail sx={{ fontSize: 16 }} />}
          sx={{
            background: 'linear-gradient(135deg, #e60108 0%, #b80006 100%)',
            py: 1,
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(230,1,8,0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Get in Touch
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        backgroundColor: getNavbarBgColor(),
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        borderBottom: scrolled ? 'none' : `1px solid ${alpha(getTextColor(), 0.06)}`,
        backdropFilter: 'blur(10px)',
        boxShadow: scrolled 
          ? (isDarkMode 
              ? '0 4px 30px rgba(0,0,0,0.3)' 
              : '0 4px 30px rgba(0,0,0,0.05)')
          : 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          py: 0, // Reduced padding
          minHeight: 'auto !important', 
          px: { xs: 2, sm: 3 },
          height: { xs: 65, sm: 70 }, // Smaller navbar height
        }}>
          {/* Logo Section - Larger Logo with smaller navbar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
          >
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  width: { xs: 160, sm: 200 }, // Much larger logo
                  height: { xs: 160, sm: 200 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  mt: { xs: -4, sm: -5 }, // Increased negative margin to pull logo up
                  mb: { xs: -4, sm: -5 },
                }}
              >
                <img 
                  src="/images/wwclogo.png" 
                  alt="Worldwide Communication Logo" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Link>
          </motion.div>

          {/* Desktop Menu */}
          {!isMobile ? (
            <Stack direction="row" spacing={isTablet ? 1 : 2} alignItems="center">
              {navLinks.slice(0, isTablet ? 5 : 7).map((link) => (
                <motion.div
                  key={link.name}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    component={NavLink}
                    to={link.path}
                    sx={{
                      color: location.pathname === link.path ? '#e60108' : getTextColor(),
                      fontWeight: location.pathname === link.path ? 600 : 500,
                      fontSize: isTablet ? '0.75rem' : '0.8rem', // Slightly smaller text
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5, // Reduced padding
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        width: location.pathname === link.path ? '30%' : '0%',
                        height: '2px',
                        backgroundColor: '#e60108',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover::after': {
                        width: '30%',
                      },
                      '&:hover': {
                        color: getHoverColor(),
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {link.name}
                  </Button>
                </motion.div>
              ))}
              
              {/* Services Dropdown */}
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  onClick={handleServicesClick}
                  endIcon={<ExpandMoreIcon sx={{ fontSize: 14, transition: 'transform 0.3s ease', transform: servicesAnchor ? 'rotate(180deg)' : 'none' }} />}
                  sx={{
                    color: getTextColor(),
                    fontWeight: 500,
                    fontSize: isTablet ? '0.75rem' : '0.8rem',
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: '0%',
                      height: '2px',
                      backgroundColor: '#e60108',
                      transition: 'all 0.3s ease',
                      transform: 'translateX(-50%)',
                    },
                    '&:hover::after': {
                      width: '30%',
                    },
                    '&:hover': {
                      color: getHoverColor(),
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Services
                </Button>
              </motion.div>

              {/* Theme Toggle */}
              <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'}>
                <motion.div
                  whileHover={{ scale: 1.1, rotate: isDarkMode ? -15 : 15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton 
                    onClick={toggleTheme} 
                    size="small"
                    sx={{
                      color: getTextColor(),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: getHoverColor(),
                        backgroundColor: alpha('#e60108', 0.1),
                      },
                    }}
                  >
                    {isDarkMode ? <LightMode sx={{ fontSize: 16 }} /> : <DarkMode sx={{ fontSize: 16 }} />}
                  </IconButton>
                </motion.div>
              </Tooltip>

              {/* Contact Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #e60108 0%, #b80006 100%)',
                    color: '#FFFFFF',
                    textTransform: 'none',
                    px: isTablet ? 2 : 2.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(230,1,8,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #b80006 0%, #8a0004 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(230,1,8,0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Stack>
          ) : (
            <motion.div
              whileTap={{ scale: 0.95 }}
            >
              <IconButton 
                onClick={handleDrawerToggle} 
                sx={{ 
                  color: getTextColor(),
                  '&:hover': {
                    backgroundColor: alpha('#e60108', 0.1),
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </motion.div>
          )}
        </Toolbar>
      </Container>

      {/* Services Dropdown Menu - Desktop */}
      <Menu
        anchorEl={servicesAnchor}
        open={Boolean(servicesAnchor)}
        onClose={handleServicesClose}
        PaperProps={{
          onMouseLeave: handleServicesClose,
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: isDarkMode 
              ? '0 20px 40px rgba(0,0,0,0.5)' 
              : '0 20px 40px rgba(0,0,0,0.1)',
            minWidth: 300,
            overflow: 'visible',
            border: `1px solid ${alpha('#e60108', 0.2)}`,
            bgcolor: isDarkMode ? '#0A0A0A' : '#1A1A1A',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -8,
              right: 20,
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: `8px solid ${alpha('#e60108', 0.3)}`,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ 
            px: 1.5, 
            py: 0.5, 
            color: '#e60108', 
            fontWeight: 700, 
            display: 'block', 
            fontSize: '0.7rem',
            letterSpacing: '0.5px',
          }}>
            OUR SERVICES
          </Typography>
          <Divider sx={{ my: 1, borderColor: alpha('#FFFFFF', 0.1) }} />
          {servicesLinks.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <MenuItem
                component={Link}
                to={service.path}
                onClick={handleServicesClose}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1.5,
                  mb: 0.5,
                  transition: 'all 0.3s ease',
                  color: '#FFFFFF',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: service.gradient,
                    opacity: hoveredService === index ? 0.1 : 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateX(8px)',
                    backgroundColor: alpha(service.color, 0.1),
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: service.color, 
                  minWidth: 36,
                  transition: 'transform 0.3s ease',
                  transform: hoveredService === index ? 'scale(1.1)' : 'scale(1)',
                }}>
                  {service.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={service.name}
                  secondary={service.description}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.65rem', color: alpha('#FFFFFF', 0.5) }}
                />
                <ChevronRight sx={{ 
                  fontSize: 16, 
                  color: alpha('#FFFFFF', 0.3),
                  transition: 'transform 0.3s ease',
                  transform: hoveredService === index ? 'translateX(4px)' : 'none',
                }} />
              </MenuItem>
            </motion.div>
          ))}
        </Box>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 300,
            borderRadius: '0',
            bgcolor: getDrawerBgColor(),
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha('#000000', 0.5),
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;